/**
 * What colour the wizard's context ring is, and why (AI-291).
 *
 * Measured against the *compaction threshold*, not the window. A thread at 60% of
 * its window is at 86% of a 0.7 threshold, and the second number is the one worth
 * reacting to: compaction is what happens next, not running out of room.
 *
 * The thresholds below are therefore fractions of `compactAtTokens`, so moving
 * COMPACT_AT_FRACTION in the wizard's settings moves the colours with it rather
 * than leaving them describing a threshold that no longer exists.
 *
 * Kept out of the SFC so it can be unit-tested directly — named exports of a .vue
 * file have no type declarations for ts-jest to resolve.
 */

/** Past this share of the way to compaction, it is worth noticing. */
export const NEAR = 0.75;

/** Past this, compaction happens within a turn or two. */
export const IMMINENT = 0.9;

export type RingTone = "filling" | "near" | "imminent";

/**
 * Brand vocabulary. This app has no design-token layer — colours are repeated as
 * literals throughout `ui/src`, so a `--warn`/`--danger` variable would be the
 * only one of its kind.
 */
const FILLING = "#50b0ff";
const WARNING = "#e6a23c";
const DANGER = "#f56c6c";

export function ringTone(
  usedTokens: number,
  compactAtTokens: number
): RingTone {
  // Guarded rather than divided by: a zero threshold would make every ring red on
  // a misconfigured deployment.
  if (compactAtTokens <= 0) return "filling";

  const ratio = usedTokens / compactAtTokens;
  if (ratio >= IMMINENT) return "imminent";
  if (ratio >= NEAR) return "near";
  return "filling";
}

export function ringColorFor(
  usedTokens: number,
  compactAtTokens: number
): string {
  switch (ringTone(usedTokens, compactAtTokens)) {
    case "imminent":
      return DANGER;
    case "near":
      return WARNING;
    default:
      return FILLING;
  }
}

/** Shown when there is no thread to describe yet. */
export const RING_INACTIVE_COLOR = "#c0c4cc";
